import * as React from "react";

import {
	Button,
	Card,
	Checkbox,
	Colors,
	Elevation,
	HTMLTable,
	MenuItem,
	NumericInput,
	Switch,
} from "@blueprintjs/core";
import { Select } from "@blueprintjs/select";
import { css } from "glamor";

import { Grade, Juror, Participant } from "src/Interfaces";

const styles = {
	grade: css({ width: "100px", height: "45px", vertigalAlign: "middle", lineHeight: "45px" }),
	name: css({
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		width: "100px",
		height: "53px",
	}),
	uneditable: (grade_counts: boolean) =>
		css({ marginLeft: "10px", color: grade_counts ? "" : Colors.GRAY5 + " !important" }),
	gradesCount: (editing: boolean) =>
		css({
			color: editing ? "" : Colors.GRAY5 + " !important",
			fontSize: "12px",
			fontStyle: "italic",
		}),
	title: css({
		fontSize: "24px",
		display: "flex",
		justifyContent: "space-between",
	}),
};

const sortJurors = (a: Juror, b: Juror) => {
	if (a.firstname.toLowerCase() > b.firstname.toLowerCase()) {
		return 1;
	} else if (
		a.firstname.toLowerCase() === b.firstname.toLowerCase() &&
		a.lastname.toLowerCase() > b.lastname.toLowerCase()
	) {
		return 1;
	}
	return -1;
};

export default class GradesCheckerCard extends React.Component<
	{
		stage: any;
		jurors: Juror[];
		editing: boolean;
		checked: boolean;
		onToggleEditing: () => void;
		onJurorSelect: (selectedJuror: Juror, old_juror_id: number) => void;
		onToggleGradesCount: (juror_id: number, gradesCount: boolean) => void;
		onSetStageChecked: () => void;
		onGradeChange: (
			newGrade: number,
			gradeData: Grade,
			gradeIndex: number,
			stageID: number,
			alreadyChecked: boolean
		) => void;
	},
	{}
> {
	private makeRow = (
		label: string,
		participant: Participant,
		jurors: Juror[],
		grades: Grade[],
		stageID: number,
		editable: boolean,
		alreadyChecked: boolean
	) => (
		<tr>
			<td {...styles.grade}>{label}</td>
			<td {...styles.grade} {...styles.name}>
				{participant && participant.firstname + " " + participant.lastname}
			</td>
			{jurors
				.sort((a: any, b: any) => a.position - b.position)
				.map((juror: any) => {
					const gradeIndex = grades.findIndex(
						(grade: any) => grade.juror_id === juror.id && grade.participant_id === participant.id
					)!;
					const gradeData = grades.find(
						(grade: any) => grade.juror_id === juror.id && grade.participant_id === participant.id
					)!;
					if (editable) {
						return (
							<td {...styles.grade} key={gradeData.juror_id}>
								<NumericInput
									onValueChange={(nr) =>
										this.props.onGradeChange(nr, gradeData, gradeIndex, stageID, alreadyChecked)
									}
									max={10}
									min={1}
									majorStepSize={1}
									selectAllOnFocus={true}
									clampValueOnBlur={true}
									value={gradeData.grade}
									buttonPosition={"none"}
									fill={true}
								/>
							</td>
						);
					} else {
						return (
							<td {...styles.grade} key={gradeData.juror_id}>
								<div {...styles.uneditable(gradeData.grade_counts)}>{gradeData.grade}</div>
							</td>
						);
					}
				})}
		</tr>
	);
	private itemRenderer(juror: Juror, { handleClick }: any) {
		return (
			<MenuItem
				key={juror.id}
				text={juror.firstname + " " + juror.lastname}
				onClick={handleClick} // added this line
				// shouldDismissPopover={true}
			/>
		);
	}

	private itemPredicate = (query: string, juror: Juror) => {
		const name = juror.firstname + " " + juror.lastname;
		const foundPattern = name.toLowerCase().includes(query.toLowerCase());
		return foundPattern;
	};
	public render() {
		const {
			stage,
			jurors,
			editing,
			checked,
			onToggleEditing,
			onJurorSelect,
			onToggleGradesCount,
			onSetStageChecked,
		} = this.props;
		const reporter = stage.participants.find(
			(participant: any) => participant.id === stage.reporter_id
		);
		const opponent = stage.participants.find(
			(participant: any) => participant.id === stage.opponent_id
		);
		const reviewer = stage.participants.find(
			(participant: any) => participant.id === stage.reviewer_id
		);
		return (
			<div>
				<Card elevation={Elevation.TWO}>
					<div {...styles.title}>
						{stage.name + " - Stage " + stage.stagenumber}
						<Switch checked={editing} label="Edit Grades" large={true} onChange={onToggleEditing} />
					</div>
					<HTMLTable>
						<thead>
							<tr>
								<th key={1} />
								<th key={2} />
								{stage.jurors
									.sort((a: any, b: any) => a.position - b.position)
									.map((juror: any) => {
										if (editing) {
											return (
												<th key={juror.lastname + juror.firstname} {...styles.name}>
													<Select
														items={jurors.sort(sortJurors)}
														itemRenderer={this.itemRenderer}
														noResults={<MenuItem text="No results." />}
														onItemSelect={(selectedJuror: Juror) =>
															onJurorSelect(selectedJuror, juror.id)
														}
														itemPredicate={this.itemPredicate}
													>
														<Button
															text={juror.firstname + " " + juror.lastname}
															rightIcon="double-caret-vertical"
														/>
													</Select>
												</th>
											);
										} else {
											return (
												<th key={juror.lastname + juror.firstname} {...styles.name}>
													{juror.firstname} {juror.lastname}
												</th>
											);
										}
									})}
							</tr>
						</thead>
						<tbody>
							{this.makeRow(
								"Reporter",
								reporter,
								stage.jurors,
								stage.grades,
								stage.id,
								editing,
								checked
							)}
							{this.makeRow(
								"Opponent",
								opponent,
								stage.jurors,
								stage.grades,
								stage.id,
								editing,
								checked
							)}
							{this.makeRow(
								"Reviewer",
								reviewer,
								stage.jurors,
								stage.grades,
								stage.id,
								editing,
								checked
							)}
							<tr>
								<td />
								<td {...styles.gradesCount(editing)}>grade counts:</td>
								{stage.jurors.map((juror: Juror) => {
									const gradesCount =
										stage.grades
											.filter((grade: Grade) => grade.juror_id === juror.id)
											.findIndex((grade: Grade) => !grade.grade_counts) === -1;
									return (
										<td key={juror.id}>
											<Checkbox
												disabled={!editing}
												checked={gradesCount}
												onChange={() => onToggleGradesCount(juror.id, gradesCount)}
											/>
										</td>
									);
								})}
							</tr>
						</tbody>
					</HTMLTable>
					{!checked ? <Button onClick={onSetStageChecked}>Ok</Button> : <div />}
				</Card>
			</div>
		);
	}
}



// WEBPACK FOOTER //
// ./src/Backend/GradesCheckerCard.tsx